import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Tooltip } from '@mui/material';
import { styled, css } from '@mui/material/styles';
import React from 'react';

import {
  formatWorkflowStatus,
  ASSESSMENT_STATUS_COLUMNS,
} from './workflow-status-labels';

import { getDesignTokens } from '~/client/components/base/core';
import {
  GenericCard,
  GenericCardHeader,
  GenericCardContent,
  GenericCardActions,
} from '~/client/components/base/generic-card';
import Text from '~/client/components/base/text';
import ProgressWithText from '~/client/components/progress-with-text';
import * as GQL from '~/graphql/generated/client';
import StatusBadge from '~c/components/base/status-badge';

type AssessmentCategoryStatusType =
  | 'N_A'
  | 'INCOMPLETE'
  | 'PASS'
  | 'FAIL'
  | undefined;

interface ProjectCardProps<DataT> {
  className?: string;
  entry: DataT;
  projectName?: (entry: DataT) => string | undefined;
  source?: (entry: DataT) => string | undefined;
  risk?: (entry: DataT) => GQL.AssessmentRiskStatusType | undefined | null;
  assessmentCompletion?: (entry: DataT) => number | undefined | null;
  policyAlignment?: (entry: DataT) => boolean | undefined;
  owner?: (entry: DataT) => string | undefined;
  description?: (entry: DataT) => string | undefined;
  isChampion?: (entry: DataT) => boolean;
  securityStatus?: (entry: DataT) => AssessmentCategoryStatusType;
  privacyStatus?: (entry: DataT) => AssessmentCategoryStatusType;
  biasStatus?: (entry: DataT) => AssessmentCategoryStatusType;
  performanceStatus?: (entry: DataT) => AssessmentCategoryStatusType;
  showTrueAssessmentStatusColumns?: boolean;
  assessmentStatusByPolicy?: (
    entry: DataT
  ) => { policyIdentifier: string; status: GQL.AssessmentStatusType | null }[];
  onClick?: (entry: DataT) => void;
}

const AssessmentCategoryIcon: React.FC<{
  status: AssessmentCategoryStatusType;
  label: string;
}> = ({ status, label }) => {
  const getIcon = () => {
    switch (status) {
      case 'PASS':
        return <CheckIcon sx={{ color: 'success.main', fontSize: 16 }} />;
      case 'FAIL':
        return <CloseIcon sx={{ color: 'error.main', fontSize: 16 }} />;
      case 'INCOMPLETE':
        return (
          <Text sz="body12" color="secondary">
            —
          </Text>
        );
      case 'N_A':
      default:
        return (
          <Text sz="body12" color="secondary">
            ?
          </Text>
        );
    }
  };

  const getTooltip = () => {
    switch (status) {
      case 'PASS':
        return `${label}: Pass`;
      case 'FAIL':
        return `${label}: Fail`;
      case 'INCOMPLETE':
        return `${label}: Incomplete`;
      case 'N_A':
      default:
        return `${label}: Unverified`;
    }
  };

  return (
    <Tooltip title={getTooltip()}>
      <Box className="category-item">
        <Text sz="body12" color="secondary">
          {label}:
        </Text>
        {getIcon()}
      </Box>
    </Tooltip>
  );
};

const getRiskBorderColor = (
  riskValue: GQL.AssessmentRiskStatusType | undefined | null
): string => {
  if (riskValue === GQL.AssessmentRiskStatusType.High) {
    return '#f44336';
  }
  if (riskValue === GQL.AssessmentRiskStatusType.Medium) {
    return '#ff9800';
  }
  if (riskValue === GQL.AssessmentRiskStatusType.Low) {
    return '#4caf50';
  }
  return '#9e9e9e';
};

function ProjectCardImpl<DataT>({
  className,
  entry,
  projectName,
  source,
  risk,
  assessmentCompletion,
  policyAlignment,
  owner,
  description,
  isChampion,
  securityStatus,
  privacyStatus,
  biasStatus,
  performanceStatus,
  showTrueAssessmentStatusColumns,
  assessmentStatusByPolicy,
  onClick,
}: ProjectCardProps<DataT>) {
  const name = projectName?.(entry) ?? '';
  const sourceValue = source?.(entry);
  const riskValue = risk?.(entry);
  const completion = assessmentCompletion?.(entry);
  const policyAlignmentValue = policyAlignment?.(entry);
  const ownerValue = owner?.(entry);
  const descriptionValue = description?.(entry);
  const championValue = isChampion?.(entry) ?? false;

  const hasSecurityData = securityStatus !== undefined;
  const hasPrivacyData = privacyStatus !== undefined;
  const hasBiasData = biasStatus !== undefined;
  const hasPerformanceData = performanceStatus !== undefined;
  const hasAnyCategory =
    hasSecurityData || hasPrivacyData || hasBiasData || hasPerformanceData;

  const getRiskBadgeValue = (): 'high' | 'medium' | 'low' | 'unavailable' => {
    if (riskValue === GQL.AssessmentRiskStatusType.High) {
      return 'high';
    }
    if (riskValue === GQL.AssessmentRiskStatusType.Medium) {
      return 'medium';
    }
    if (riskValue === GQL.AssessmentRiskStatusType.Low) {
      return 'low';
    }
    return 'unavailable';
  };

  const handleClick = () => {
    onClick?.(entry);
  };

  const truncatedDescription =
    descriptionValue && descriptionValue.length > 80
      ? `${descriptionValue.slice(0, 80)}…`
      : descriptionValue;

  return (
    <GenericCard
      variant="minimal"
      className={className}
      size="custom"
      width="100%"
      onClick={handleClick}
      sx={{
        borderLeft: `3px solid ${getRiskBorderColor(riskValue)}`,
        minHeight: '140px',
      }}
    >
      <GenericCardHeader
        title={
          <Box className="card-title-row">
            <Box className="name-champion-row">
              <Text sz="bold-body16" className="project-name">
                {name}
              </Text>
              {championValue && (
                <Tooltip title="Champion Model">
                  <EmojiEventsIcon
                    className="champion-star-icon"
                    sx={{ color: '#f5a623', fontSize: 18, ml: 0.5 }}
                  />
                </Tooltip>
              )}
            </Box>
            <KeyboardArrowRightIcon className="arrow-icon" />
          </Box>
        }
        action={null}
      />
      <GenericCardContent>
        <Box className="card-body">
          {truncatedDescription && (
            <Text sz="body12" color="secondary" className="description-text">
              {truncatedDescription}
            </Text>
          )}

          {sourceValue && (
            <Text sz="body12" color="secondary" className="source-text">
              Source: {sourceValue}
            </Text>
          )}

          {ownerValue && (
            <Text sz="body12" color="secondary">
              Owner: {ownerValue}
            </Text>
          )}

          <Box className="risk-progress-row">
            {riskValue && (
              <StatusBadge variant="risk" value={getRiskBadgeValue()} />
            )}
            {policyAlignment !== undefined &&
              policyAlignmentValue !== undefined && (
                <StatusBadge
                  variant="alignment"
                  value={policyAlignmentValue ? 'aligned' : 'not-aligned'}
                />
              )}
            {!showTrueAssessmentStatusColumns && (
              <Box className="progress-wrapper">
                <ProgressWithText value={completion} />
              </Box>
            )}
          </Box>

          {showTrueAssessmentStatusColumns && (
            <Box className="categories-row">
              {(() => {
                const statuses = assessmentStatusByPolicy?.(entry) ?? [];
                return ASSESSMENT_STATUS_COLUMNS.map((col) => (
                  <Box key={col.label} className="category-item">
                    <Text sz="body12" color="secondary">
                      {col.label}:
                    </Text>
                    <Text sz="body12">
                      {formatWorkflowStatus(
                        statuses[col.index]?.status ?? null
                      )}
                    </Text>
                  </Box>
                ));
              })()}
            </Box>
          )}

          {hasAnyCategory && (
            <Box className="categories-row">
              {hasSecurityData && (
                <AssessmentCategoryIcon
                  status={securityStatus?.(entry)}
                  label="Security"
                />
              )}
              {hasPrivacyData && (
                <AssessmentCategoryIcon
                  status={privacyStatus?.(entry)}
                  label="Privacy"
                />
              )}
              {hasBiasData && (
                <AssessmentCategoryIcon
                  status={biasStatus?.(entry)}
                  label="Bias"
                />
              )}
              {hasPerformanceData && (
                <AssessmentCategoryIcon
                  status={performanceStatus?.(entry)}
                  label="Performance"
                />
              )}
            </Box>
          )}
        </Box>
      </GenericCardContent>
      <GenericCardActions alignment="right">
        <Box />
      </GenericCardActions>
    </GenericCard>
  );
}

const ProjectCard = styled(ProjectCardImpl)(({ theme }) => {
  const t = getDesignTokens(theme.palette.mode, theme.accentPalette);
  return css`
    &.MuiCard-root {
      border: 1px solid ${t.borderSubtle};
      width: 100%;
      cursor: pointer;
      transition: border-color 0.2s ease;
      min-height: 140px;

      &:hover {
        border-color: ${t.borderDefault};
      }

      &:active {
        background-color: ${t.surface2};
      }
    }

    .card-title-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }

    .name-champion-row {
      display: flex;
      align-items: center;
      flex: 1;
      min-width: 0;
    }

    .project-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .champion-star-icon {
      flex-shrink: 0;
    }

    .arrow-icon {
      color: ${t.textSecondary};
      flex-shrink: 0;
    }

    .card-body {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(1.5)};
    }

    .description-text {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .source-text {
      margin-bottom: ${theme.spacing(0.5)};
    }

    .risk-progress-row {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(1)};
      flex-wrap: wrap;
    }

    .progress-wrapper {
      flex: 1;
      min-width: 0;
    }

    .categories-row {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(2)};
      flex-wrap: wrap;
      padding-top: ${theme.spacing(1)};
      border-top: 1px solid ${t.borderSubtle};
    }

    .category-item {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(0.5)};
    }
  `;
}) as typeof ProjectCardImpl;

export default ProjectCard;
